<template>
  <div class="main-container">
    <div class="controls-wrapper pa-4">
      <v-text-field
        v-if="false"
        v-model="searchQuery"
        dark
        solo
        dense
        rounded
        hide-details
        clearable
        placeholder="Übung suchen..."
        prepend-inner-icon="mdi-magnify"
        class="mb-4"></v-text-field>

      <div class="d-flex align-center mt-3">
        <v-select v-model="sortBy" :items="sortOptions" label="Sortieren nach" dark dense hide-details outlined class="sort-select"></v-select>
        <v-btn icon dark @click="sortDesc = !sortDesc" class="ml-2">
          <v-icon>{{ sortDesc ? "mdi-sort-descending" : "mdi-sort-ascending" }}</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="explorer-container" ref="explorerContainer">
      <div v-if="processedExercises.length === 0" class="no-results-container">
        <v-icon large color="grey lighten-1">mdi-magnify-close</v-icon>
        <p class="mt-4 grey--text text--lighten-1">Keine Ergebnisse gefunden.</p>
        <p class="grey--text text--darken-1 text-caption">Versuche, deine Filter anzupassen.</p>
      </div>

      <div
        v-for="exercise in visibleExercises"
        :key="exercise.exercise._id"
        class="exercise-slide rounded-xl"
        :ref="(el) => setItemRef(el, exercise.exercise._id)">
        <div class="slide-background" :style="{ backgroundImage: `url(${getImageForExercise(exercise.exercise)})` }"></div>

        <v-btn icon dark class="info-button" @click="$emit('view-exercise', exercise, $event)">
          <v-icon>mdi-information-outline</v-icon>
        </v-btn>

        <div class="slide-content">
          <div class="header">
            <h2 class="exercise-title">{{ exercise.exercise.title }}</h2>
            <p class="exercise-muscle-group">{{ getExerciseTagsString(exercise.exercise) }}</p>
          </div>

          <div class="content-body">
            <div v-if="getPersonalRecords(exercise).length > 0" class="pr-card animated-child">
              <div class="pr-icon">{{ getPersonalRecords(exercise)[0].icon }}</div>
              <div class="pr-details">
                <span class="pr-label">{{ getPersonalRecords(exercise)[0].label }}</span>
                <span class="pr-value">
                  {{ getPersonalRecords(exercise)[0].value }} <span class="unit">{{ getPersonalRecords(exercise)[0].unit }}</span>
                </span>
              </div>
            </div>

            <div class="chart-wrapper animated-child" style="--delay: 0.1s">
              <h4 class="chart-title">Dein Fortschritt</h4>
              <apexchart
                v-if="chartData[exercise.exercise._id] && chartData[exercise.exercise._id].series"
                type="area"
                height="150"
                :options="getChartOptions(exercise.exercise._id)"
                :series="chartData[exercise.exercise._id].series"></apexchart>
              <p v-else class="no-chart-data">Nicht genügend Daten für den Graphen.</p>
            </div>
          </div>
        </div>
      </div>

      <div ref="scrollTrigger" v-if="visibleCount < processedExercises.length" class="scroll-trigger"></div>
    </div>
  </div>
</template>

<script>
import moment from "moment"
import VueApexCharts from "vue-apexcharts"

const INITIAL_LOAD_COUNT = 5
const LOAD_MORE_COUNT = 5

export default {
  name: "ExerciseExplorer",
  components: { apexchart: VueApexCharts },
  props: {
    exercises: { type: Array, required: true },
  },
  data() {
    return {
      itemRefs: {},
      observer: null,
      scrollObserver: null,
      visibleCount: INITIAL_LOAD_COUNT,
      searchQuery: "",
      // Status für Filter und Sortierung
      selectedTags: [],
      sortBy: "default",
      sortDesc: true,
      sortOptions: [
        { text: "Standard", value: "default" },
        { text: "Anzahl Ausführungen", value: "executions" },
        { text: "Maximalgewicht", value: "maxWeight" },
        { text: "Maximaldistanz", value: "maxDistance" }, // NEU

        { text: "Maximaldauer", value: "maxDuration" },
      ],
    }
  },
  computed: {
    // Ermittelt alle einzigartigen Tags für die Filter-Chips
    allTags() {
      const tags = new Set()
      this.exercises.forEach((ex) => {
        if (ex.exercise.tags) {
          ex.exercise.tags.forEach((tag) => tags.add(tag.name))
        }
      })
      return Array.from(tags).sort()
    },
    processedExercises() {
      let tempExercises = [...this.exercises]

      // Filtern von "Senso Pro"
      tempExercises = tempExercises.filter((ex) => ex.exercise && ex.exercise.title !== "Senso Pro")

      if (this.sortBy === "maxDistance") {
        // NEU
        tempExercises = tempExercises.filter((ex) => ex.summary && ex.summary.totalDistance > 0)
      }
      // NEU: Filtern basierend auf dem Sortiertyp
      if (this.sortBy === "maxDuration") {
        tempExercises = tempExercises.filter((ex) => this.isDurationExercise(ex))
      } else if (this.sortBy === "maxWeight") {
        // Zeigt nur Übungen, die tatsächlich Gewicht haben
        tempExercises = tempExercises.filter((ex) => ex.summary && ex.summary.totalWeight > 0)
      }

      // 2. Nach Suchbegriff filtern
      if (this.searchQuery) {
        const lowerCaseQuery = this.searchQuery.toLowerCase()
        tempExercises = tempExercises.filter((ex) => ex.exercise.title.toLowerCase().includes(lowerCaseQuery))
      }

      // 3. Sortieren
      if (this.sortBy !== "default") {
        tempExercises.sort((a, b) => {
          let valA = 0
          let valB = 0

          if (this.sortBy === "executions") {
            valA = a.sets?.length || 0
            valB = b.sets?.length || 0
          } else if (this.sortBy === "maxDistance") {
            // NEU
            valA = a.summary?.maxDistanceStep?.distance || 0
            valB = b.summary?.maxDistanceStep?.distance || 0
          } else if (this.sortBy === "maxWeight") {
            valA = a.summary?.maxWeightStep?.weight || 0
            valB = b.summary?.maxWeightStep?.weight || 0
          } else if (this.sortBy === "maxDuration") {
            // Sortiert nach der Gesamtdauer
            valA = a.summary?.totalDuration || 0
            valB = b.summary?.totalDuration || 0
          }

          if (this.sortDesc) {
            return valB - valA
          } else {
            return valA - valB
          }
        })
      }

      return tempExercises
    },

    // `visibleExercises` nimmt jetzt die `processedExercises` als Basis
    visibleExercises() {
      console.log(this.processedExercises[0])
      return this.processedExercises.slice(0, this.visibleCount)
    },

    chartData() {
      const allChartData = {}
      this.exercises.forEach((ex) => {
        if (!ex.sets || ex.sets.length < 2) {
          allChartData[ex.exercise._id] = null
          return
        }
        const isDistance = this.isDistanceExercise(ex)
        const isDuration = this.isDurationExercise(ex)
        const dataByDate = {}
        ex.sets.forEach((s) => {
          const date = moment(s.date).format("YYYY-MM-DD")
          if (!dataByDate[date]) dataByDate[date] = []
          if (s.steps) dataByDate[date].push(...s.steps)
        })

        const sortedDates = Object.keys(dataByDate).sort((a, b) => new Date(a) - new Date(b))

        const seriesData = sortedDates.map((date) => {
          const dailySteps = dataByDate[date]
          let yValue

          if (isDistance) {
            yValue = Math.max(0, ...dailySteps.map((step) => step.distance || 0))
          } else if (isDuration) {
            yValue = Math.max(0, ...dailySteps.map((step) => this.parseDurationToSeconds(step.time) || 0))
          } else {
            yValue = Math.max(0, ...dailySteps.map((step) => step.weight || 0))
          }
          return { x: date, y: yValue }
        })

        if (seriesData.length < 2) {
          allChartData[ex.exercise._id] = null
        } else {
          allChartData[ex.exercise._id] = {
            series: [{ name: isDistance ? "Distanz" : isDuration ? "Dauer" : "Max. Gewicht", data: seriesData }],
            isDistance: isDistance, // NEU
            isDuration: isDuration,
          }
        }
      })
      return allChartData
    },
  },
  watch: {
    // Beobachtet alle Filter- und Sortieränderungen über `processedExercises`.
    // Setzt die Ansicht zurück, wenn sich die Ergebnisliste ändert.
    processedExercises() {
      // NEU: Zuerst den alten Observer trennen, um Leaks zu vermeiden
      if (this.scrollObserver) {
        this.scrollObserver.disconnect()
      }

      this.visibleCount = INITIAL_LOAD_COUNT // Setze die sichtbaren Elemente zurück
      this.$nextTick(() => {
        if (this.$refs.explorerContainer) {
          this.$refs.explorerContainer.scrollTop = 0 // Scrolle nach ganz oben
        }
        this.itemRefs = {} // Referenzen zurücksetzen für Animationen
        this.$nextTick(() => {
          this.observeItemsForAnimation()
          // NEU & WICHTIG: Den Scroll-Trigger-Observer neu starten
          this.observeScrollTrigger()
        })
      })
    },
    visibleExercises() {
      this.$nextTick(() => {
        this.observeItemsForAnimation()
      })
    },
  },
  methods: {
    isDistanceExercise(exercise) {
      // NEUE HILFSFUNKTION
      return exercise.summary && exercise.summary.totalDistance > 0
    },
    formatDuration(seconds) {
      if (!seconds || seconds <= 0) return "00:00"
      return moment.utc(seconds * 1000).format("HH:mm:ss")
    },

    parseDurationToSeconds(timeString) {
      if (!timeString || typeof timeString !== "string") return 0
      return moment.duration(timeString).asSeconds()
    },

    isDurationExercise(exercise) {
      return exercise.summary && exercise.summary.totalDuration > 0 && exercise.summary.totalWeight === 0
    },

    getPersonalRecords(exercise) {
      if (exercise.summary) {
        if (this.isDistanceExercise(exercise) && exercise.summary.maxDistanceStep) {
          return [
            {
              label: "Maximale Distanz",
              value: exercise.summary.maxDistanceStep.distance,
              unit: "m",
              icon: "🏃‍♂️",
            },
          ]
        }

        if (exercise.summary.maxWeightStep && exercise.summary.maxWeightStep.weight > 0) {
          return [
            {
              label: "Maximalgewicht",
              value: exercise.summary.maxWeightStep.weight,
              unit: "kg",
              icon: "🏆",
            },
          ]
        }
        if (exercise.summary.totalDuration && exercise.summary.totalDuration > 0) {
          return [
            {
              // GEÄNDERT: Label für mehr Klarheit
              label: "Gesamtdauer",
              value: this.formatDuration(exercise.summary.totalDuration),
              unit: "Stunden",
              icon: "⏱️",
            },
          ]
        }
      }
      return []
    },
    getChartOptions(exerciseId) {
      const chartInfo = this.chartData[exerciseId]
      const isDuration = chartInfo ? chartInfo.isDuration : false
      const isDistance = chartInfo ? chartInfo.isDistance : false // NEU

      const tooltipFormatter = (val) => {
        if (isDistance) return `${val} m` // NEU

        if (isDuration) {
          return `${this.formatDuration(val)} min`
        }
        return `${val} kg`
      }

      return {
        chart: { type: "area", height: 150, sparkline: { enabled: true } },
        colors: ["#00E396"],
        stroke: { curve: "smooth", width: 3 },
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.1, stops: [0, 90, 100] } },
        tooltip: { enabled: true, theme: "dark", x: { format: "dd MMM yy" }, y: { formatter: tooltipFormatter } },
        xaxis: { type: "datetime" },
      }
    },
    loadMore() {
      if (this.visibleCount < this.processedExercises.length) {
        this.visibleCount += LOAD_MORE_COUNT
      }
    },
    observeScrollTrigger() {
      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.loadMore()
          }
        },
        { rootMargin: "200px" }
      )
      if (this.$refs.scrollTrigger) {
        this.scrollObserver.observe(this.$refs.scrollTrigger)
      }
    },
    setItemRef(el, id) {
      if (el) {
        this.itemRefs[id] = el
      }
    },
    observeItemsForAnimation() {
      if (!this.observer) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add("is-visible")
              }
            })
          },
          { threshold: 0.4 }
        )
      }
      Object.values(this.itemRefs).forEach((item) => {
        if (item) this.observer.observe(item)
      })
    },
    getImageForExercise(ex) {
      if (!ex) return ""
      if (ex.picture) return `/img/uebungen/${ex.picture.replace(/\.[^/.]+$/, "")}.webp`
      if (ex.manual && ex.manual[0]?.picture) return `/img/uebungen/${ex.manual[0].picture.replace(/\.[^/.]+$/, "")}.webp`
      return ""
    },
    getExerciseTagsString(ex) {
      if (!ex || !ex.tags || ex.tags.length === 0) return "Allgemein"
      return ex.tags.map((tag) => tag.name).join(", ")
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.observeItemsForAnimation()
      this.observeScrollTrigger()
    })
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
    }
  },
}
</script>

<style scoped>
.main-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.controls-wrapper {
  flex-shrink: 0;
  background-color: rgb(30, 30, 30);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
::v-deep .controls-wrapper .v-input__control .v-input__slot {
  background-color: rgba(45, 45, 45, 1) !important;
}
::v-deep .v-chip.v-chip--active {
  background-color: #00e396 !important;
  color: #121212 !important;
  border-color: #00e396 !important;
}
.sort-select {
  max-width: 220px;
}
.explorer-container {
  flex-grow: 1;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  scroll-behavior: smooth;
  position: relative;
}
.no-results-container {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}
.scroll-trigger {
  height: 50px;
}
.exercise-slide {
  width: 100%;
  position: relative;
  scroll-snap-align: start;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
}
.exercise-slide.is-visible .animated-child {
  opacity: 1;
  transform: translateY(0);
}
.slide-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  filter: blur(3px) brightness(0.5);
  transform: scale(1.1);
}
.info-button {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.3);
}
.slide-content {
  position: relative;
  z-index: 1;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}
.header {
  text-align: center;
  padding: 16px 0 8px 0;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
}
.exercise-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
}
.exercise-muscle-group {
  font-size: 0.8rem;
  opacity: 0.8;
  margin-bottom: 12px;
}
.content-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.pr-card,
.chart-wrapper {
  background: rgba(30, 30, 30, 0.5);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 16px;
}
.pr-card {
  display: flex;
  align-items: center;
  gap: 16px;
}
.animated-child {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease, transform 0.5s ease;
  transition-delay: var(--delay, 0s);
}
.pr-icon {
  font-size: 2rem;
}
.pr-label {
  display: block;
  font-size: 0.7rem;
  opacity: 0.7;
  text-transform: uppercase;
}
.pr-value {
  font-size: 1.8rem;
  font-weight: 700;
}
.unit {
  font-size: 1rem;
  opacity: 0.7;
  font-weight: 400;
}
.chart-title {
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 8px;
  opacity: 0.8;
}
.no-chart-data {
  text-align: center;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.4);
  padding: 10px 0;
}
</style>
